import { Card } from '@/components/ui';
import { formatPhone } from '@/lib/phoneUtils';
import type { SupplierLocationView } from '../types';

interface SupplierLocationCardProps {
  location: SupplierLocationView;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function SupplierLocationCard({
  location,
  onEdit,
  onDelete,
}: SupplierLocationCardProps) {
  const displayName = location.locationName || 'Primary Location';

  return (
    <Card className="hover:shadow-md transition-shadow">
      <div className="p-4">
        {/* Header Section */}
        <div className="flex items-start justify-between mb-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-medium text-secondary-900 truncate">
              {displayName}
            </h3>
          </div>

          {/* Action Buttons */}
          {(onEdit || onDelete) && (
            <div className="flex items-center gap-1 ml-2 flex-shrink-0">
              {onEdit && (
                <button
                  type="button"
                  onClick={onEdit}
                  className="p-1.5 text-secondary-400 hover:text-primary-600
                             hover:bg-primary-50 rounded transition-colors"
                  title="Edit location"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  onClick={onDelete}
                  className="p-1.5 text-secondary-400 hover:text-red-600
                             hover:bg-red-50 rounded transition-colors"
                  title="Delete location"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Location Details */}
        <div className="space-y-2 text-sm">
          {/* Address */}
          <div className="flex items-start gap-2">
            <svg
              className="w-4 h-4 text-secondary-400 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            {location.fullAddress ? (
              <span className="text-secondary-600">{location.fullAddress}</span>
            ) : (
              <span className="text-secondary-400 italic">No address</span>
            )}
          </div>

          {/* Contact Name */}
          {location.contactFullName && location.contactFullName.trim() && (
            <div className="flex items-center gap-2">
              <svg
                className="w-4 h-4 text-secondary-400 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              <span className="text-secondary-600">{location.contactFullName}</span>
            </div>
          )}

          {/* Phone */}
          {location.phone && (
            <div className="flex items-center gap-2">
              <svg
                className="w-4 h-4 text-secondary-400 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              <span className="text-secondary-600">{formatPhone(location.phone)}</span>
            </div>
          )}

          {/* Email */}
          {location.email && (
            <div className="flex items-center gap-2">
              <svg
                className="w-4 h-4 text-secondary-400 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <a
                href={`mailto:${location.email}`}
                className="text-primary-600 hover:underline truncate"
              >
                {location.email}
              </a>
            </div>
          )}
        </div>

      </div>
    </Card>
  );
}
